import React, { useMemo, useState } from 'react'
import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, TextField, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Link as RouterLink } from 'react-router-dom'
import { C, RADIUS, SX, TYPE } from '../../merchants/shared/tokens'
import { fmtDate } from '../constants'

/*
 * Shared building blocks of the PTL Merchant Setup pages, on the Merchant Setup design tokens (slate ink, one indigo
 * accent, semantic state colours used only for state) so these pages read as part of the same module.
 */

/** Text for screen readers only. Sizes are px strings: in MUI sx a bare 1 means 100%. */
export const SR_ONLY = { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }

export const merchantLabel = (m) => (m ? (m.companyName && m.companyName !== m.name ? `${m.name} (${m.companyName})` : m.name) : '')

/** Page title block: eyebrow, title, one-line purpose, and the page's primary actions on the right. */
export function PageHeader({ title, description, actions, eyebrow = 'PTL Merchant Setup' }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, mb: 2.5 }}>
      <Box sx={{ flex: '1 1 420px', minWidth: 0 }}>
        <Typography sx={{ ...TYPE.eyebrow, color: C.inkFaint, mb: 0.5 }}>{eyebrow}</Typography>
        <Typography component='h1' sx={{ ...TYPE.pageTitle, fontSize: 22 }}>{title}</Typography>
        {description && <Typography sx={{ ...TYPE.rowHelp, fontSize: 13, mt: 0.5, maxWidth: 820 }}>{description}</Typography>}
      </Box>
      {actions && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{actions}</Box>}
    </Box>
  )
}

/** A section's one-line purpose and its actions, under the section tabs of a page (PtlPage with tabs). */
export function SectionIntro({ description, actions }) {
  if (!description && !actions) return null
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
      {description && <Typography sx={{ ...TYPE.rowHelp, fontSize: 13, flex: '1 1 420px', maxWidth: 820 }}>{description}</Typography>}
      {actions && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 'auto' }}>{actions}</Box>}
    </Box>
  )
}

/**
 * Underlined tabs with an optional count pill, e.g. Standard TAT | Custom TAT (1). An item with `to` is a link to its
 * own route (page sections: bookmarkable, open in a new tab); without it, `onChange` switches the tab in place.
 */
export function SegmentTabs({ value, onChange, items, ariaLabel }) {
  return (
    <Tabs value={value} onChange={onChange ? (_, v) => onChange(v) : undefined} aria-label={ariaLabel} variant='scrollable' allowScrollButtonsMobile
      sx={{
        mb: 2, minHeight: 44, borderBottom: `1px solid ${C.line}`,
        '& .MuiTabs-indicator': { bgcolor: C.accent, height: 2 },
        '& .MuiTab-root': { textTransform: 'none', minHeight: 44, px: 1.5, mr: 1, fontSize: 13.5, fontWeight: 600, color: C.inkMuted },
        '& .MuiTab-root:hover': { color: C.ink },
        '& .MuiTab-root.Mui-focusVisible': { outline: `2px solid ${C.accent}`, outlineOffset: -2 },
        '& .Mui-selected': { color: `${C.ink} !important` },
      }}>
      {items.map((t) => (
        <Tab key={t.key} value={t.key} disableRipple {...(t.to ? { component: RouterLink, to: t.to } : {})} label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {t.label}
            {t.count !== undefined && (
              <Box component='span' sx={{ ...SX.pill, color: value === t.key ? C.accent : C.inkFaint, bgcolor: value === t.key ? C.accentSoft : C.surfaceAlt }}>
                {t.count}
              </Box>
            )}
          </Box>
        } />
      ))}
    </Tabs>
  )
}

/** "Merchant overrides › Staging Test" with the parent clickable. */
export function Breadcrumb({ parent, onParent, current, chip }) {
  return (
    <Box component='nav' aria-label='Breadcrumb' sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
      <Button size='small' onClick={onParent} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13, color: C.inkMuted, px: 0.75, minWidth: 0, '&:hover': { color: C.accent, bgcolor: C.accentSoft } }}>
        {parent}
      </Button>
      <ChevronRightIcon sx={{ fontSize: 18, color: C.inkGhost }} aria-hidden />
      <Typography aria-current='page' sx={{ ...TYPE.rowLabel, fontSize: 14 }}>{current}</Typography>
      {chip && <Box sx={{ ml: 1 }}>{chip}</Box>}
    </Box>
  )
}

const TONE = {
  info: { fg: C.info, bg: C.infoSoft, border: C.infoBorder, Icon: InfoOutlinedIcon },
  warn: { fg: C.warn, bg: C.warnSoft, border: C.warnBorder, Icon: WarningAmberOutlinedIcon },
  danger: { fg: C.danger, bg: C.dangerSoft, border: C.dangerBorder, Icon: ErrorOutlineIcon },
}

/** One-line state banner with an icon (state never shown by colour alone). */
export function Banner({ tone = 'info', children, action }) {
  const t = TONE[tone] || TONE.info
  return (
    <Box role={tone === 'danger' ? 'alert' : 'status'} sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 1.75, py: 1.25, mb: 2,
      bgcolor: t.bg, border: `1px solid ${t.border}`, borderRadius: RADIUS.md,
    }}>
      <t.Icon sx={{ fontSize: 18, color: t.fg, mt: '1px' }} aria-hidden />
      <Typography sx={{ fontSize: 13, color: C.inkBody, lineHeight: 1.55, flex: 1 }}>{children}</Typography>
      {action}
    </Box>
  )
}

/** KPI tile. */
export function Kpi({ label, value, sub }) {
  return (
    <Box sx={{ ...SX.card, minWidth: 0, px: 1.75, py: 1.5 }}>
      <Typography sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.ink, letterSpacing: '-0.02em', mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {sub && <Box sx={{ fontSize: 11.5, color: C.inkFaint, mt: 0.25 }}>{sub}</Box>}
    </Box>
  )
}

/**
 * Pincodes per zone as filter tiles, grouped by zone family (the letters of the zone name: N, C, W, S, E, NE), each with
 * its exact count and a share bar. Zones that are not standard zones (9999, ids the standard TAT does not define) go in
 * a separate "Needs attention" row so data problems never hide among the zones.
 *
 * @param zones     [{ zoneId, name, area, pincodes }] from the summary API
 * @param selected  zone id currently filtered on ('' = none)
 * @param standardIds set of zone ids the standard TAT defines
 */
export function ZoneDistribution({ zones, selected, onPick, standardIds, hideEmpty }) {
  const total = zones.reduce((s, z) => s + (z.pincodes || 0), 0) || 1
  const max = Math.max(1, ...zones.filter((z) => standardIds.has(z.zoneId)).map((z) => z.pincodes || 0))
  const shown = zones.filter((z) => !hideEmpty || z.pincodes > 0)
  const families = []
  for (const z of shown.filter((x) => standardIds.has(x.zoneId))) {
    const key = ((z.name || '').match(/^[A-Za-z]+/) || [z.zoneId.charAt(0)])[0].toUpperCase()
    let f = families.find((x) => x.key === key)
    if (!f) families.push((f = { key, zones: [] }))
    f.zones.push(z)
  }
  const issues = shown.filter((z) => !standardIds.has(z.zoneId) && z.pincodes > 0)

  const tile = (z, issue) => {
    const on = !!z.zoneId && selected === z.zoneId
    const share = (z.pincodes || 0) / total
    return (
      <Box key={z.zoneId || 'none'} component='button' type='button' disabled={!z.zoneId} aria-pressed={on}
        onClick={() => z.zoneId && onPick(z.zoneId)}
        title={z.area || ''}
        sx={{
          all: 'unset', boxSizing: 'border-box', cursor: z.zoneId ? 'pointer' : 'default', minHeight: 56,
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 0.5, p: 1.1,
          borderRadius: RADIUS.sm, transition: 'border-color 150ms, background-color 150ms',
          border: `1px solid ${on ? C.accent : issue ? C.warnBorder : C.line}`,
          bgcolor: on ? C.accentSoft : issue ? C.warnSoft : C.surface,
          '&:hover': z.zoneId ? { borderColor: on ? C.accent : C.accentBorder } : {},
          '&:focus-visible': { outline: `2px solid ${C.accent}`, outlineOffset: 2 },
        }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography component='span' sx={{ fontSize: 13, fontWeight: 700, color: on ? C.accent : issue ? C.warn : C.ink, whiteSpace: 'nowrap' }}>
            {z.name || z.zoneId}
          </Typography>
          <Typography component='span' sx={{ fontSize: 13, fontWeight: 700, color: C.inkBody, fontVariantNumeric: 'tabular-nums' }}>
            {(z.pincodes || 0).toLocaleString('en-IN')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography component='span' sx={{ fontSize: 10.5, color: C.inkFaint, fontVariantNumeric: 'tabular-nums', minWidth: 30 }}>{z.zoneId || '—'}</Typography>
          {!issue && (
            <Box aria-hidden sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: C.lineSoft, overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.max(2, ((z.pincodes || 0) / max) * 100)}%`, height: '100%', bgcolor: on ? C.accent : C.accentBorder }} />
            </Box>
          )}
          <Typography component='span' sx={{ fontSize: 10.5, color: C.inkFaint, fontVariantNumeric: 'tabular-nums', ml: issue ? 'auto' : 0 }}>
            {share >= 0.001 ? `${(share * 100).toFixed(1)}%` : '<0.1%'}
          </Typography>
        </Box>
      </Box>
    )
  }

  // Families sit side by side (2-4 zones each), so 16 zones fit in two short rows instead of six.
  const familyLabel = (f) => (f.zones.length > 1 ? `${f.zones[0].name} – ${f.zones[f.zones.length - 1].name}` : f.zones[0].name)
  const block = (key, label, items, issue) => (
    <Box key={key} sx={{ minWidth: 0 }}>
      <Typography sx={{ ...TYPE.eyebrow, color: issue ? C.warn : C.inkFaint, mb: 0.75 }}>{label}</Typography>
      <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>{items.map((z) => tile(z, issue))}</Box>
    </Box>
  )
  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
      {families.map((f) => block(f.key, familyLabel(f), f.zones, false))}
      {issues.length > 0 && block('issues', 'Needs attention', issues, true)}
    </Box>
  )
}

/** Last-change line used in tables (from the audit log): "18 Oct 2026, 11:28 pm · ajeet@…". */
export const changeText = (v) => (v ? `${fmtDate(v.createdAt)}${v.createdBy ? ` · ${v.createdBy}` : ''}` : 'Never changed')

/** Pick a PTL merchant to add an override for (merchants that already have one are listed above, not here). */
export function MerchantPickerDialog({ open, onClose, merchants, onPick, title, helper }) {
  const [picked, setPicked] = useState(null)
  const options = useMemo(() => merchants || [], [merchants])
  const close = () => { setPicked(null); onClose() }
  return (
    <Dialog open={open} onClose={close} maxWidth='xs' fullWidth>
      <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ ...TYPE.rowHelp, mb: 2 }}>{helper}</Typography>
        <Autocomplete autoHighlight options={options} value={picked} onChange={(_, m) => setPicked(m)}
          getOptionLabel={merchantLabel} isOptionEqualToValue={(o, v) => o.id === v.id}
          noOptionsText='Every PTL merchant already has one'
          renderInput={(p) => <TextField {...p} autoFocus label='PTL merchant' />} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
        <Button variant='contained' disabled={!picked} onClick={() => { const m = picked; setPicked(null); onPick(m) }} sx={SX.cta}>Continue</Button>
      </DialogActions>
    </Dialog>
  )
}
